import { useEffect, useState } from 'react'

import { useStripe } from '@stripe/react-stripe-js'

import { PaymentMethod } from '@/enums/payments'
import { canMakePayment } from '@/utils/stripeUtils'

type TPaymentMethodsAvailable = Partial<Record<PaymentMethod, boolean>>

export const useStripeWalletsAvaible = (
  overwriteAllowed: boolean,
  maxDelay: number = 1000
): [boolean, TPaymentMethodsAvailable | null] => {
  const stripe = useStripe()
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [walletsAvailable, setWalletsAvailable] = useState<TPaymentMethodsAvailable | null>(null)

  useEffect(() => {
    if (isLoading || !stripe || !!walletsAvailable) {
      return
    }

    setIsLoading(true)

    const stripeRequest = canMakePayment(stripe)
      .then(
        (result) => {
          if (!overwriteAllowed && walletsAvailable != null) {
            return
          }

          setWalletsAvailable((prevState) => {
            if (!overwriteAllowed && prevState != null) {
              return prevState
            }
            return result || {}
          })
        },
        () => {
          setWalletsAvailable({})
        }
      )
      .finally(() => {
        setIsLoading(false)
      })

    const timerRequest = new Promise((resolve) => {
      setTimeout(() => {
        setWalletsAvailable((prevState) => {
          // do not overwrite
          return prevState || {}
        })
        resolve(undefined)
      }, maxDelay)
    })

    Promise.any([stripeRequest, timerRequest]).finally(() => {
      setIsLoading(false)
    })
  }, [stripe, isLoading, walletsAvailable, overwriteAllowed, maxDelay])

  return [isLoading, walletsAvailable]
}
